<template>
  <ul :class="[listClass, listVariant ? `menu-link-list--${listVariant}` : '']" role="list">
    <li v-for="(item, index) in visibleItems" :key="index" role="listitem">
      <component
        :is="wrapperComponent(item)"
        :to="item.to"
        :href="item.href"
        :target="item.target"
        :class="itemClass"
        :aria-label="item.ariaLabel"
        tabindex="0"
        role="link"
        @click="onItemClick(item, index)"
      >
        <component
          :is="item.icon"
          v-if="item.icon"
          :class="item.iconClasses || iconSpacerClass"
          :color="iconColor"
          :width="item.iconWidth ?? iconSize"
          :height="item.iconHeight ?? iconSize"
          aria-hidden="true"
        />
        <span :class="item.labelClasses">{{ item.label }}</span>
        <span v-if="$slots['item-right']" class="menu-link-list__right">
          <slot name="item-right" :item="item" />
        </span>
      </component>
    </li>
  </ul>
</template>

<script lang="ts">
/**
 * MenuLinkList renders a list of menu items as links only. Each item must have `href` or `to`;
 * items with neither are omitted. Each item can show an optional icon and label.
 *
 * Item type: `to` → <router-link>, `href` → <a>.
 * When filterByVisible is true (default), items with `visible: false` are omitted. When false,
 * all items are shown and `visible` is only used as item data (e.g. for item-right slot).
 *
 * Slot: item-right — optional content on the right (e.g. badge, trailing icon); scope: { item }. Automatically pushed right via margin-left: auto.
 * Event: item-click(item, index) — emitted when an item is clicked (after optional item.click).
 */
import { PropType } from 'vue';
import { RouterLink } from 'vue-router';
import type { MenuItem } from './MenuLinkListTypes';
import { markItemsComponentsRaw } from '@/helpers/markComponentRaw';

export default {
  name: 'MenuLinkList',
  components: { RouterLink },
  props: {
    /** List of menu items; see MenuLinkListTypes.MenuItem. When filterByVisible is true, items with visible: false are hidden. */
    items: {
      type: Array as PropType<MenuItem[]>,
      required: true,
    },
    /** Passed to item icons (e.g. fill color). */
    iconColor: {
      type: String,
      default: '#4B4C52',
    },
    /** Default width/height for item icons; overridable per item via iconWidth/iconHeight. */
    iconSize: {
      type: Number,
      default: 24,
    },
    /** CSS classes applied to the <ul>. */
    listClass: {
      type: String,
      default: '',
    },
    /** CSS classes applied to each link. Default includes padding/border so consumers can override via Tailwind (e.g. pl-12). */
    itemClass: {
      type: String,
      default: 'flex items-center border-2 border-white py-8 px-0',
    },
    /** Default class for the icon wrapper when item has no iconClasses. */
    iconSpacerClass: {
      type: String,
      default: 'mr-8',
    },
    /** Deprecated: variant styles no longer supported. Use listClass and itemClass for styling. */
    listVariant: {
      type: String,
      default: '',
    },
    /** When true (default), items with visible: false are not rendered. When false, all items are shown. */
    filterByVisible: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    visibleItems(): MenuItem[] {
      const filtered = this.filterByVisible ? this.items.filter((item) => item.visible !== false) : this.items;
      return markItemsComponentsRaw(filtered.filter((item) => item.href != null || item.to != null));
    },
  },
  methods: {
    wrapperComponent(item: MenuItem) {
      if (item.to) return RouterLink;
      return 'a';
    },
    onItemClick(item: MenuItem, index: number): void {
      item.click?.();
      this.$emit('item-click', item, index);
    },
  },
};
</script>

<style lang="scss" scoped>
ul {
  border-radius: 4px;
  background: theme('colors.white');
}

li {
  display: block;
  width: 100%;
}

li a {
  width: 100%;
  display: flex;
  align-items: center;
  white-space: pre;
  box-sizing: border-box;
  border: 2px solid transparent; // avoids layout shift on hover

  > *:first-child svg {
    margin-right: 1rem;
  }

  .menu-link-list__right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
    min-width: 0;
  }

  &:hover,
  &:focus-visible {
    cursor: pointer;
    border-color: theme('colors.grey.background.contrast');
    background: theme('colors.grey.background.contrast');
    color: theme('colors.brand.contrast');

    svg {
      filter: invert(9%) sepia(100%) saturate(5180%) hue-rotate(328deg) brightness(70%) contrast(108%);
    }
  }

  &:focus-visible {
    outline: 2px dotted theme('colors.brand.contrast');
  }
}
</style>
